import { Input } from "@/components/ui/input";
import type { ModelSchema, SchemaFeature } from "@/features/inference/queries";
import { type ChangeEvent, useState } from "react";

export type FormValue = Record<string, string | number | boolean | null>;

export interface SchemaFormProps {
  schema: ModelSchema;
  value: FormValue;
  onChange: (next: FormValue) => void;
}

function coerce(dtype: SchemaFeature["dtype"], raw: string): string | number | boolean | null {
  if (raw === "") return null;
  if (dtype === "int") {
    const n = Number.parseInt(raw, 10);
    return Number.isNaN(n) ? raw : n;
  }
  if (dtype === "float") {
    const n = Number.parseFloat(raw);
    return Number.isNaN(n) ? raw : n;
  }
  if (dtype === "bool") return raw === "true";
  return raw;
}

function hintFor(dtype: SchemaFeature["dtype"]): string {
  return dtype;
}

export function SchemaForm({ schema, value, onChange }: SchemaFormProps) {
  // Internal raw string buffer so the user can type freely (e.g. "99.5") without
  // React's controlled-input reset clobbering in-progress keystrokes.
  const [raw, setRaw] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of schema.features) {
      const v = value[f.name];
      init[f.name] = v == null ? "" : String(v);
    }
    return init;
  });

  const handleChange = (name: string, _dtype: SchemaFeature["dtype"], str: string) => {
    const next = { ...raw, [name]: str };
    setRaw(next);
    const coerced: FormValue = {};
    for (const f of schema.features) {
      coerced[f.name] = coerce(f.dtype, next[f.name] ?? "");
    }
    onChange(coerced);
  };

  return (
    <div className="space-y-2.5">
      {schema.features.map((f) => {
        const display = raw[f.name] ?? "";
        return (
          <div key={f.name} className="grid grid-cols-[150px_1fr] items-center gap-3">
            <label
              htmlFor={`f-${f.name}`}
              className="text-xs text-fg-muted inline-flex items-center gap-1.5"
            >
              <span>
                {f.name}
                {f.required && <span className="text-danger"> *</span>}
              </span>
              <span className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-fg-dim">
                {hintFor(f.dtype)}
              </span>
            </label>
            {f.dtype === "bool" ? (
              <select
                id={`f-${f.name}`}
                className="h-8 w-full rounded-md border border-border-strong bg-surface-2 px-2 text-xs"
                value={display}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  handleChange(f.name, f.dtype, e.target.value)
                }
              >
                <option value="">—</option>
                <option value="false">false</option>
                <option value="true">true</option>
              </select>
            ) : f.dtype === "date" || f.dtype === "datetime" ? (
              <Input
                id={`f-${f.name}`}
                type={f.dtype === "date" ? "date" : "datetime-local"}
                value={display}
                onChange={(e) => handleChange(f.name, f.dtype, e.target.value)}
              />
            ) : (
              <Input
                id={`f-${f.name}`}
                type="text"
                inputMode={f.dtype === "int" ? "numeric" : f.dtype === "float" ? "decimal" : "text"}
                value={display}
                onChange={(e) => handleChange(f.name, f.dtype, e.target.value)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
